import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { Suspense, lazy, useEffect } from 'react'
import { ConfigProvider, Spin } from 'antd'
import ErrorBoundary from './components/common/ErrorBoundary'
import ProtectedRoute from './components/common/ProtectedRoute'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'
import { lightTheme, darkTheme } from './config/theme'
// import { configureBerlixTheme } from './utils/formUtils'
import './styles/animations.css'
import './styles/touch-optimizations.css'
import './styles/browser-fixes.css'
import './styles/performance-optimizations.css'
import './styles/accessibility-fixes.css'
import './styles/logo-quality.css'
import './styles/theme-fixes.css'

// Layouts
import PublicLayout from './layouts/PublicLayout'
import AuthLayout from './layouts/AuthLayout'
import AdminLayout from './layouts/AdminLayout'

// Public pages (keep critical pages eager loaded for better initial experience)
import HomePage from './pages/public/HomePage'
import PricingPage from './pages/public/PricingPage'
import CaseLookupPage from './pages/public/CaseLookupPage'

// Lazy load legal pages (rarely visited)
const PrivacyPage = lazy(() => import('./pages/public/PrivacyPage'))
const CookiePage = lazy(() => import('./pages/public/CookiePage'))

// Company portal for anonymous reporters
const CompanyPortal = lazy(() => import('./pages/portal/CompanyPortal'))

// Auth pages
import LoginPage from './pages/auth/LoginPage'

// Registration page
import RegistrationPage from './pages/public/RegistrationPage'

// Error pages
import NotFoundPage from './pages/NotFoundPage'

// Lazy load admin pages for better performance
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage'))
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage-responsive'))
const ReportDetailPage = lazy(() => import('./pages/admin/ReportDetailPage-responsive'))
const UsersPage = lazy(() => import('./pages/admin/UsersPage-responsive'))
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'))
const TestSuitePage = lazy(() => import('./pages/admin/TestSuitePage'))
const TestAuthPage = lazy(() => import('./pages/admin/TestAuthPage'))
const AnalyticsPage = lazy(() => import('./pages/admin/AnalyticsPage'))
const ResponsiveTestPage = lazy(() => import('./pages/admin/ResponsiveTestPage'))
const PerformanceMonitor = lazy(() => import('./pages/admin/PerformanceMonitor'))
const MobilePerformanceTest = lazy(() => import('./pages/admin/MobilePerformanceTest'))
const AccessibilityTest = lazy(() => import('./pages/admin/AccessibilityTest'))
const ScreenReaderTest = lazy(() => import('./pages/admin/ScreenReaderTest'))
const ResponsiveTestDashboard = lazy(() => import('./pages/admin/ResponsiveTestDashboard'))
const SecurityAuditPage = lazy(() => import('./pages/admin/SecurityAuditPage'))
const BackendCheckPage = lazy(() => import('./pages/admin/BackendCheckPage'))
const StorageBucketTest = lazy(() => import('./pages/admin/StorageBucketTest'))
const EmailTestPage = lazy(() => import('./pages/admin/EmailTestPage'))
const OnboardingPage = lazy(() => import('./pages/admin/OnboardingPage'))
const TestDebugPage = lazy(() => import('./pages/admin/TestDebugPage'))
const DebugRPCPage = lazy(() => import('./pages/admin/DebugRPCPage'))
const TestOrganizationPage = lazy(() => import('./pages/admin/TestOrganizationPage'))
const TestAllOrganizationsPage = lazy(() => import('./pages/admin/TestAllOrganizationsPage'))
const SupabaseDebugPage = lazy(() => import('./pages/admin/SupabaseDebugPage'))
const CompanyPortalTest = lazy(() => import('./pages/admin/CompanyPortalTest'))

// Loading fallback component
const PageLoader = () => (
  <div className="flex justify-center items-center h-96">
    <Spin size="large" tip="Loading..." />
  </div>
)

// Main app content with theme-aware ConfigProvider
const AppContent = () => {
  const { theme } = useTheme()
  
  useEffect(() => {
    // configureBerlixTheme()
    
    // Apply performance optimizations
    import('./utils/performanceMonitor').then(({ applyMobileOptimizations }) => {
      applyMobileOptimizations()
    })
    
    // Initialize Web Vitals monitoring
    import('./utils/webVitals').then(({ initWebVitals, observeResourceTiming }) => {
      initWebVitals()
      observeResourceTiming()
    })
    
    // Initialize mobile optimizations
    import('./utils/mobilePerformance').then(({ initMobileOptimizations }) => {
      initMobileOptimizations()
    })
  }, [])
  
  return (
    <ConfigProvider theme={theme === 'dark' ? darkTheme : lightTheme}>
      <BrowserRouter>
        <Routes>
        {/* Company Portal routes - for anonymous reporters */}
        <Route path="/portal/:companySlug" element={<Suspense fallback={<PageLoader />}><CompanyPortal /></Suspense>} />
        
        {/* Public routes */}
        <Route path="/" element={<PublicLayout />}>
          <Route index element={<HomePage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="register" element={<RegistrationPage />} />
          <Route path="lookup" element={<CaseLookupPage />} />
          <Route path="case/:caseId" element={<CaseLookupPage />} />
          <Route path="privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
          <Route path="cookies" element={<Suspense fallback={<PageLoader />}><CookiePage /></Suspense>} />
        </Route>
        
        {/* Auth routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
        </Route>
        
        {/* Admin routes - protected */}
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>} />
          <Route path="onboarding" element={<Suspense fallback={<PageLoader />}><OnboardingPage /></Suspense>} />
          <Route path="reports" element={<Suspense fallback={<PageLoader />}><ReportsPage /></Suspense>} />
          <Route path="reports/:reportId" element={<Suspense fallback={<PageLoader />}><ReportDetailPage /></Suspense>} />
          <Route path="analytics" element={<Suspense fallback={<PageLoader />}><AnalyticsPage /></Suspense>} />
          <Route path="users" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><UsersPage /></Suspense></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><SettingsPage /></Suspense></ProtectedRoute>} />
          <Route path="test-suite" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><TestSuitePage /></Suspense></ProtectedRoute>} />
          <Route path="test-auth" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><TestAuthPage /></Suspense></ProtectedRoute>} />
          <Route path="responsive-test" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><ResponsiveTestPage /></Suspense></ProtectedRoute>} />
          <Route path="performance" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><PerformanceMonitor /></Suspense></ProtectedRoute>} />
          <Route path="mobile-performance" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><MobilePerformanceTest /></Suspense></ProtectedRoute>} />
          <Route path="accessibility" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><AccessibilityTest /></Suspense></ProtectedRoute>} />
          <Route path="screen-reader" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><ScreenReaderTest /></Suspense></ProtectedRoute>} />
          <Route path="responsive-dashboard" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><ResponsiveTestDashboard /></Suspense></ProtectedRoute>} />
          <Route path="security-audit" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><SecurityAuditPage /></Suspense></ProtectedRoute>} />
          <Route path="backend-check" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><BackendCheckPage /></Suspense></ProtectedRoute>} />
          <Route path="storage-test" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><StorageBucketTest /></Suspense></ProtectedRoute>} />
          <Route path="email-test" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><EmailTestPage /></Suspense></ProtectedRoute>} />
          <Route path="test-debug" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><TestDebugPage /></Suspense></ProtectedRoute>} />
          <Route path="debug-rpc" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><DebugRPCPage /></Suspense></ProtectedRoute>} />
          <Route path="test-organization" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><TestOrganizationPage /></Suspense></ProtectedRoute>} />
          <Route path="test-all-organizations" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><TestAllOrganizationsPage /></Suspense></ProtectedRoute>} />
          <Route path="supabase-debug" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><SupabaseDebugPage /></Suspense></ProtectedRoute>} />
          <Route path="portal-test" element={<ProtectedRoute requiredRole="admin"><Suspense fallback={<PageLoader />}><CompanyPortalTest /></Suspense></ProtectedRoute>} />
        </Route>
        
        {/* 404 page */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App